import {
  Box,
  styled,
  Typography,
  Button,
  TableCell,
  Table,
  TableHead,
  TableRow,
  Paper,
  TableContainer,
  TableBody,
  Modal,
  TextField,
  IconButton,
} from '@mui/material';
import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import DeleteWarningModal from 'src/components/DeleteWarningModal';
import useLoadDataList from 'src/hook/useLoadDataList';
import {
  deleteAssemblyCategory,
  getAssemblyCategories,
  updateAssemblyCategory,
  reorderAssemblyCategories,
  AssemblyCategory,
} from 'src/services/assembly-category';
import { handleApiError } from 'src/utils/api.utils';

type SortableRowProps = {
  category: AssemblyCategory;
  onEdit: (category: AssemblyCategory) => void;
  onDelete: (id: number) => void;
};

const SortableRow = ({ category, onEdit, onDelete }: SortableRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: category.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isDragging ? '#f5f5f5' : 'inherit',
  };

  return (
    <TableRow ref={setNodeRef} style={style}>
      <DragHandle {...attributes} {...listeners}>
        <DragIndicatorIcon sx={{ color: 'grey.500' }} />
      </DragHandle>
      <TableCell sx={{ width: '10%' }}>{category.id}</TableCell>
      <TableCell sx={{ width: '45%' }}>{category.name}</TableCell>
      <TableCell sx={{ width: '15%' }}>{category._count?.assemblies ?? 0}</TableCell>
      <TableCell sx={{ width: '25%' }}>
        <ActionsContainer>
          <Button variant="contained" color="primary" size="small" onClick={() => onEdit(category)}>
            <EditIcon fontSize="small" sx={{ mr: 0.5 }} />
            Modifier
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={() => onDelete(category.id)}
            disabled={(category._count?.assemblies ?? 0) > 0}
          >
            Supprimer
          </Button>
        </ActionsContainer>
      </TableCell>
    </TableRow>
  );
};

const DragHandle = styled(TableCell)({
  width: '5%',
  cursor: 'grab',
});

const ActionsContainer = styled('div')({
  display: 'flex',
  gap: 8,
});

export const ListCategories = () => {
  const { data: categories, onRefresh, setData } = useLoadDataList(getAssemblyCategories);
  const [editingCategory, setEditingCategory] = useState<AssemblyCategory | null>(null);
  const [editName, setEditName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<number>();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  useEffect(() => {
    if (editingCategory) {
      setEditName(editingCategory.name);
    }
  }, [editingCategory]);

  const handleDelete = async () => {
    if (categoryToDelete === undefined) return;
    try {
      await deleteAssemblyCategory(categoryToDelete);
      toast.success('Catégorie supprimée avec succès');
      onRefresh();
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleEdit = (category: AssemblyCategory) => {
    setEditingCategory(category);
  };

  const handleCloseModal = () => {
    setEditingCategory(null);
    setEditName('');
  };

  const handleSubmitEdit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!editingCategory) return;

    if (!editName.trim()) {
      toast.error('Le nom est requis');
      return;
    }

    setIsSubmitting(true);
    try {
      await updateAssemblyCategory(editingCategory.id, editName.trim());
      toast.success('Catégorie modifiée avec succès');
      handleCloseModal();
      onRefresh();
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = categories.findIndex((cat) => cat.id === active.id);
      const newIndex = categories.findIndex((cat) => cat.id === over.id);

      const newOrder = arrayMove(categories, oldIndex, newIndex);
      setData(newOrder);

      try {
        await reorderAssemblyCategories(newOrder.map((cat) => cat.id));
        toast.success('Ordre des catégories mis à jour');
      } catch (error) {
        handleApiError(error);
        onRefresh();
      }
    }
  };

  return (
    <Container>
      <Typography variant="h6" gutterBottom>
        Liste des catégories
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Glissez-déposez les lignes pour réorganiser l&apos;ordre des catégories
      </Typography>

      <TableContainer component={Paper}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '5%' }} />
                <TableCell sx={{ width: '10%' }}>ID</TableCell>
                <TableCell sx={{ width: '45%' }}>Nom</TableCell>
                <TableCell sx={{ width: '15%' }}>Assemblages</TableCell>
                <TableCell sx={{ width: '25%' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <SortableContext
                items={categories.map((c) => c.id)}
                strategy={verticalListSortingStrategy}
              >
                {categories?.map((category) => (
                  <SortableRow
                    key={category.id}
                    category={category}
                    onEdit={handleEdit}
                    onDelete={setCategoryToDelete}
                  />
                ))}
              </SortableContext>
              {categories?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Aucune catégorie
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DndContext>
      </TableContainer>

      <DeleteWarningModal
        handleDelete={handleDelete}
        header="Supprimer cette catégorie ?"
        onClose={() => setCategoryToDelete(undefined)}
        open={categoryToDelete !== undefined}
      />

      <Modal open={!!editingCategory} onClose={handleCloseModal}>
        <ModalContent>
          <ModalHeader>
            <Typography variant="h6">Modifier la catégorie</Typography>
            <IconButton onClick={handleCloseModal} size="small">
              <CloseIcon />
            </IconButton>
          </ModalHeader>
          <Form onSubmit={handleSubmitEdit}>
            <TextField
              label="Nom de la catégorie"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              fullWidth
              required
              autoFocus
            />
            <ModalActions>
              <Button variant="outlined" onClick={handleCloseModal}>
                Annuler
              </Button>
              <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
                {isSubmitting ? 'Modification...' : 'Modifier'}
              </Button>
            </ModalActions>
          </Form>
        </ModalContent>
      </Modal>
    </Container>
  );
};

const Container = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  padding: '20px',
});

const ModalContent = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  backgroundColor: 'white',
  borderRadius: 8,
  padding: 24,
  minWidth: 400,
  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
});

const ModalHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 24,
});

const Form = styled('form')({
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
});

const ModalActions = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 12,
});
